/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — screen 3, Review Queue
 * Purpose      : The provider's work queue of pending tenant change requests.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * §9 (tabs)      | 05-Aug-2026 | Claude         | Extracted `QueueProvenanceGapBanner` and
 *                                                 `ReviewQueueTable` as named exports so
 *                                                 `TemplateList`'s new Template Review tab can
 *                                                 reuse them instead of duplicating the queue's
 *                                                 banner text and table markup. This page's own
 *                                                 rendering (route, layout, refresh button) is
 *                                                 unchanged — it still works standalone.
 * Comms remove   | 06-Aug-2026 | Claude         | Removed `QueueProvenanceGapBanner` entirely, at
 * provenance                                       the user's request — the "Requesting tenant is
 * banner                                           not shown" notice no longer renders here or on
 *                                                 `TemplateList`'s Template Review tab (its only
 *                                                 other call site, removed in the same change; see
 *                                                 that file's own revision history). The DTO gap
 *                                                 the banner described is real and UNCHANGED — see
 *                                                 "RECORDED GAP" below, still accurate — only its
 *                                                 on-screen disclosure is gone.
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import { AsyncBoundary, useAsyncResource } from './AsyncStates';
import { fetchReviewQueue } from './MessageTemplateService';
import {
  TemplateSummary,
  channelLabel,
  requestTypeLabel,
  requestTypeOf,
} from './CommunicationTypes';
import { extractTokens } from './templateDiff';
import { isKnownToken } from './CommunicationTokenCatalog';
import { TemplateVersionHistory } from './TemplateVersionHistory';
import { TemplateReviewThread } from './TemplateReviewThread';

/**
 * A WORK QUEUE, NOT A LIST.
 *
 * This screen exists only in provider web — tenant web has no equivalent and can only
 * display an outcome once a decision has been made. So this is the single place any
 * pending tenant change request is visible to anyone, which sets the bar: a reviewer must
 * be able to TRIAGE from this table without opening rows one at a time.
 *
 * What a reviewer needs to triage, and where each item comes from:
 *
 *   - POSITION IN QUEUE  — the server returns oldest-first and this table NEVER re-sorts.
 *     Column 1 is the queue position, so "oldest first" is visible rather than implied.
 *   - REQUEST TYPE       — new template vs revision, derived from the version number
 *                          (version 1 is by definition the first). Colour-coded, because
 *                          a revision changes something already live for every tenant and
 *                          a new template does not.
 *   - CHANNEL            — an SMS body and an email body are read very differently.
 *   - BLAST RADIUS FLAG  — a revision is marked "live for all tenants on approve". This is
 *                          the fact most worth surfacing before anyone opens anything.
 *   - TOKEN RISK         — rows whose proposed body contains an UNCATALOGUED token are
 *                          flagged here, not just in Review Detail. Such a template sends
 *                          with a blank where the token was, and catching it at triage is
 *                          cheaper than catching it after approval.
 *   - BODY PREVIEW       — first ~90 chars, so obvious junk can be rejected without a click.
 *
 * ---------------------------- REQUESTING TENANT -----------------------------
 * The "Tenant" column shows WHICH tenant raised each request (a tenant schema-id such as
 * `tn24047750`, not a company name). The value comes from the change request's
 * `submitted_by_tenant`, which the review-queue endpoint now sets on `TemplateSummaryDTO`
 * during its enrichment pass (`MessageTemplateApi.reviewQueue`) — no schema or query change
 * was needed, the data was already loaded on the entity.
 *
 * STILL A GAP: `submitted_by_user` and `submitted_at` remain dropped by `toSummaryDTO`, so the
 * requesting USER and the request AGE/date are not yet shown; and the tenant value is a raw
 * schema-id, not a resolved company name (2026-08-06: an earlier `QueueProvenanceGapBanner`
 * disclosure was removed at the user's request — see the revision history below).
 * -----------------------------------------------------------------------------
 */

const BODY_PREVIEW_CHARS = 90;

export const bodyPreview = (body: string | null | undefined): string => {
  if (body == null) return '';
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length <= BODY_PREVIEW_CHARS ? flat : `${flat.slice(0, BODY_PREVIEW_CHARS)}…`;
};

/** Uncatalogued tokens in a proposed body — these render to nothing at send time. */
export const unknownTokensIn = (body: string | null | undefined): string[] =>
  extractTokens(body).filter((token) => !isKnownToken(token));

/**
 * The triage table itself, pulled out of the page so the Message Templates screen's Template
 * Review tab can embed it directly rather than duplicating the columns/flags logic. Purely
 * presentational — `rows` and the click handler are owned by the caller; this component does
 * no fetching of its own (both `ReviewQueue`, below, and the embedded tab already have exactly
 * one `useAsyncResource` between them, so this never becomes a second, competing fetch).
 */
export const ReviewQueueTable: React.FC<{
  rows: TemplateSummary[];
  onOpenDetail: (row: TemplateSummary) => void;
}> = ({ rows, onOpenDetail }) => {
  const [historyTarget, setHistoryTarget] = React.useState<TemplateSummary | null>(null);
  const [threadTarget, setThreadTarget] = React.useState<TemplateSummary | null>(null);
  return (
  <>
  <TableContainer component={Paper} variant="outlined">
    <Table size="small" aria-label="Pending template change requests">
      <TableHead>
        <TableRow>
          <TableCell width={60}>#</TableCell>
          <TableCell>Template</TableCell>
          <TableCell width={130}>Tenant</TableCell>
          <TableCell width={150}>Request type</TableCell>
          <TableCell width={90}>Channel</TableCell>
          <TableCell>Proposed body</TableCell>
          <TableCell width={150}>Flags</TableCell>
          <TableCell width={110} align="right">
            Action
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => {
          const unknown = unknownTokensIn(row.body);
          // A DELETION_REQUEST is a tenant asking to REMOVE the template. It rides the queue as a
          // vN+1 content-identical copy, so the version number alone would mislabel it as a revision —
          // key the label off the real requestKind the backend put on the row instead.
          const isDeletion = row.requestKind === 'DELETION_REQUEST';
          const isRevision = !isDeletion && requestTypeOf(row.version) === 'REVISION';
          const typeLabel = isDeletion ? 'Deletion request' : requestTypeLabel(row.version);
          return (
            <TableRow
              key={`${row.templateMasterUid}-${row.version}`}
              hover
              data-testid={`queue-row-${row.templateMasterUid}-${row.version}`}
            >
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {index + 1}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{row.name}</Typography>
                <Typography variant="caption" color="text.disabled">
                  v{row.version}
                </Typography>
              </TableCell>
              <TableCell>
                {/* WHICH tenant raised this request (provenance) — a tenant schema-id such as
                    tn24047750, not a company name. Em-dash when the queue row has no request origin. */}
                <Typography
                  variant="body2"
                  data-testid={`queue-tenant-${row.templateMasterUid}-${row.version}`}
                >
                  {row.submittedByTenant || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={typeLabel}
                  color={isDeletion ? 'error' : isRevision ? 'warning' : 'default'}
                  variant={isDeletion || isRevision ? 'filled' : 'outlined'}
                  data-testid={`queue-request-type-${row.templateMasterUid}-${row.version}`}
                />
              </TableCell>
              <TableCell>{channelLabel(row.channel)}</TableCell>
              <TableCell>
                {isDeletion ? (
                  <Typography
                    variant="body2"
                    color="error"
                    data-testid={`queue-deletion-reason-${row.templateMasterUid}-${row.version}`}
                  >
                    Reason: {row.requestReason || '—'}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {bodyPreview(row.body)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {/* 2026-08-11: the "All tenants" flag chip was removed at the user's request. The
                      bad-token flag stays — it warns of a template that will send blank text. */}
                  {unknown.length > 0 && (
                    <Tooltip
                      title={`Uncatalogued token(s): ${unknown.join(', ')} — these send as blank text`}
                    >
                      <Chip
                        size="small"
                        color="error"
                        label={`${unknown.length} bad token`}
                        data-testid={`queue-bad-token-${row.templateMasterUid}`}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  <Tooltip title="Version history">
                    <IconButton
                      size="small"
                      aria-label="Version history"
                      onClick={() => setHistoryTarget(row)}
                      data-testid={`queue-history-${row.templateMasterUid}-${row.version}`}
                    >
                      <HistoryOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Review thread">
                    <IconButton
                      size="small"
                      aria-label="Review thread"
                      onClick={() => setThreadTarget(row)}
                      data-testid={`queue-thread-${row.templateMasterUid}-${row.version}`}
                    >
                      <ForumOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button size="small" variant="contained" onClick={() => onOpenDetail(row)}>
                    Review
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
  {historyTarget && (
    <TemplateVersionHistory template={historyTarget} onClose={() => setHistoryTarget(null)} />
  )}
  {threadTarget && (
    <TemplateReviewThread template={threadTarget} onClose={() => setThreadTarget(null)} />
  )}
  </>
  );
};

export const ReviewQueue: React.FC = () => {
  const { accessToken } = useOidcAccessToken();
  const navigate = useNavigate();

  const { state, reload } = useAsyncResource<TemplateSummary[]>(
    () => fetchReviewQueue(accessToken),
    [accessToken],
  );

  const openDetail = useCallback(
    (row: TemplateSummary) => {
      // The queue row already carries the full proposed content, so Review Detail is
      // handed it via router state rather than refetching a row it cannot fetch by uid
      // (there is no GET /{uid} on provider-api).
      navigate(`/communications/review/${row.templateMasterUid}/${row.version}`, { state: { row } });
    },
    [navigate],
  );

  return (
    <Box sx={{ p: 3 }} data-testid="review-queue">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h5">Template review queue</Typography>
          <Typography variant="body2" color="text.secondary">
            Pending tenant change requests, oldest first. Approving makes a template live for
            every tenant at once.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={reload}>
          Refresh
        </Button>
      </Stack>

      <AsyncBoundary
        state={state}
        what="The template review queue"
        onRetry={reload}
        loadingLabel="Loading pending change requests…"
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No pending change requests"
        emptyDetail="Every tenant submission has been reviewed. This is a confirmed empty queue, not a failed load."
      >
        {(rows) => <ReviewQueueTable rows={rows} onOpenDetail={openDetail} />}
      </AsyncBoundary>
    </Box>
  );
};

export default ReviewQueue;
